import nacl from 'tweetnacl';
import naclUtil from 'tweetnacl-util';
import { createGame } from '../chess/game.js';
import { applySignedGameAction } from '../chess/protocol.js';

export function canonicalize(value) {
  if (Array.isArray(value)) {
    return `[${value.map(canonicalize).join(',')}]`;
  }
  if (value && typeof value === 'object') {
    return `{${Object.keys(value)
      .filter((key) => value[key] !== undefined)
      .sort()
      .map((key) => `${JSON.stringify(key)}:${canonicalize(value[key])}`)
      .join(',')}}`;
  }
  return JSON.stringify(value);
}

export async function sha512(value) {
  const bytes = new TextEncoder().encode(
    typeof value === 'string' ? value : canonicalize(value)
  );
  const digest = await crypto.subtle.digest('SHA-512', bytes);
  return Array.from(new Uint8Array(digest), (byte) =>
    byte.toString(16).padStart(2, '0')
  ).join('');
}

function unsignedCertificate(certificate) {
  const { signatures: _signatures, certificateHash: _certificateHash, ...unsigned } =
    certificate;
  return unsigned;
}

function playerEntry(player) {
  return {
    playerId: player.playerId,
    publicKey: player.publicKey
  };
}

export async function gameContractHash(game) {
  return sha512({
    gameId: game.id,
    players: [playerEntry(game.players.white), playerEntry(game.players.black)],
    timeControl: game.timeControl,
    variant: game.variant || 'standard',
    rated: game.rated,
    initialFen: game.initialFen,
    startedAt: game.startedAt
  });
}

export async function gameTranscriptHash(game) {
  return sha512({
    gameId: game.id,
    moves: game.moves.map((move) => ({
      ply: move.ply,
      actionId: move.actionId,
      signature: move.signature,
      san: move.san,
      fenAfter: move.fenAfter,
      clockAfterMs: move.clockAfterMs,
      playedAt: move.playedAt,
      signedAction: move.signedAction
    }))
  });
}

export async function createResultCertificateDraft({
  game,
  chainHeads = {},
  vdfProofs = {}
}) {
  if (!game?.players?.white || !game?.players?.black) {
    throw new Error('A result certificate requires two seated players');
  }
  if (game.status !== 'finished' || !game.result) {
    throw new Error('A result certificate requires a finished game');
  }
  if (game.result.endReason === 'abandoned') {
    throw new Error('Abandoned games require a claim certificate');
  }

  const players = [
    playerEntry(game.players.white),
    playerEntry(game.players.black)
  ];
  const certificate = {
    schemaVersion: 1,
    gameId: game.id,
    gameHash: await gameTranscriptHash(game),
    players,
    contractHash: await gameContractHash(game),
    timeControl: game.timeControl,
    variant: game.variant || 'standard',
    rated: Boolean(game.rated),
    result: game.result.value,
    endReason: game.result.endReason,
    moveCount: game.moves.length,
    startTs: game.startedAt,
    endTs: game.result.endedAt,
    vdfProofs: {
      [players[0].playerId]: vdfProofs[players[0].playerId] || null,
      [players[1].playerId]: vdfProofs[players[1].playerId] || null
    },
    chainPrev: {
      [players[0].playerId]: chainHeads[players[0].playerId] || null,
      [players[1].playerId]: chainHeads[players[1].playerId] || null
    },
    signatures: {}
  };

  return certificate;
}

export function signResultCertificate(certificate, identity) {
  if (!certificate.players.some((player) => player.playerId === identity.playerId)) {
    throw new Error('Certificate signer is not a player');
  }
  const signature = nacl.sign.detached(
    naclUtil.decodeUTF8(canonicalize(unsignedCertificate(certificate))),
    identity.keyPair.signing.secretKey
  );
  return {
    ...certificate,
    signatures: {
      ...certificate.signatures,
      [identity.playerId]: naclUtil.encodeBase64(signature)
    }
  };
}

export function verifyCertificateSignature(certificate, player) {
  const signature = certificate?.signatures?.[player?.playerId];
  const publicKey = player?.publicKey?.signingPublicKey;
  if (!signature || !publicKey) {
    return false;
  }
  try {
    return nacl.sign.detached.verify(
      naclUtil.decodeUTF8(canonicalize(unsignedCertificate(certificate))),
      naclUtil.decodeBase64(signature),
      naclUtil.decodeBase64(publicKey)
    );
  } catch {
    return false;
  }
}

export async function certificateHash(certificate) {
  return sha512({
    ...unsignedCertificate(certificate),
    signatures: certificate.signatures
  });
}

export async function finalizeResultCertificate(certificate, verificationOptions = {}) {
  const verification = await verifyResultCertificate(certificate, verificationOptions);
  if (!verification.valid) {
    throw new Error(`Cannot finalize certificate: ${verification.errors.join(', ')}`);
  }
  const finalized = {
    ...certificate,
    certificateHash: await certificateHash(certificate)
  };
  return deepFreeze(finalized);
}

export async function verifyResultCertificate(
  certificate,
  { game = null, verifyVdfProof = null } = {}
) {
  const errors = [];
  if (certificate?.schemaVersion !== 1) {
    errors.push('unsupported-schema');
  }
  if (!Array.isArray(certificate?.players) || certificate.players.length !== 2) {
    errors.push('invalid-players');
  }
  if (!['1-0', '0-1', '½-½'].includes(certificate?.result)) {
    errors.push('invalid-result');
  }
  if (!Number.isInteger(certificate?.moveCount) || certificate.moveCount < 0) {
    errors.push('invalid-move-count');
  }
  if (!(certificate?.endTs >= certificate?.startTs)) {
    errors.push('invalid-timestamps');
  }

  for (const player of certificate?.players || []) {
    if (!verifyCertificateSignature(certificate, player)) {
      errors.push(`invalid-signature:${player?.playerId || 'unknown'}`);
    }
    if (certificate.rated) {
      const proof = certificate.vdfProofs?.[player.playerId];
      if (!proof) {
        errors.push(`missing-vdf:${player.playerId}`);
      } else if (verifyVdfProof && !(await verifyVdfProof(proof, certificate, player))) {
        errors.push(`invalid-vdf:${player.playerId}`);
      }
    }
  }

  if (game) {
    if (certificate.gameId !== game.id) {
      errors.push('game-id-mismatch');
    }
    if (certificate.gameHash !== (await gameTranscriptHash(game))) {
      errors.push('game-hash-mismatch');
    }
    if (certificate.contractHash !== (await gameContractHash(game))) {
      errors.push('contract-hash-mismatch');
    }
    if (certificate.moveCount !== game.moves.length) {
      errors.push('move-count-mismatch');
    }
    if (
      certificate.result !== game.result?.value
      || certificate.endReason !== game.result?.endReason
    ) {
      errors.push('result-mismatch');
    }
    if (certificate.rated && !verifySignedMoveTranscript(game)) {
      errors.push('invalid-move-transcript');
    }
  }

  if (
    certificate?.certificateHash
    && certificate.certificateHash !== (await certificateHash(certificate))
  ) {
    errors.push('certificate-hash-mismatch');
  }

  return { valid: errors.length === 0, errors };
}

export function verifySignedMoveTranscript(game) {
  if (!game?.players?.white || !game?.players?.black) {
    return false;
  }
  if (game.moves.length === 0) {
    return true;
  }
  if (game.moves.some((move) => !move.signedAction)) {
    return false;
  }

  try {
    let replay = createGame({
      id: game.id,
      white: game.players.white,
      black: game.players.black,
      timeControl: game.timeControl,
      rated: game.rated,
      variant: game.variant,
      initialFen: game.initialFen,
      now: game.startedAt
    });
    replay = {
      ...replay,
      revision: game.moves[0].signedAction.baseRevision
    };
    for (const expectedMove of game.moves) {
      replay = applySignedGameAction(replay, expectedMove.signedAction);
      const actualMove = replay.moves.at(-1);
      if (
        actualMove.san !== expectedMove.san
        || actualMove.fenAfter !== expectedMove.fenAfter
        || actualMove.signature !== expectedMove.signature
      ) {
        return false;
      }
    }
    return replay.fen === game.fen;
  } catch {
    return false;
  }
}

export function findCertificateChainForks(certificates, playerId) {
  const byPrevious = new Map();
  const forks = [];
  for (const certificate of certificates) {
    if (!certificate.players?.some((player) => player.playerId === playerId)) {
      continue;
    }
    const previous = certificate.chainPrev?.[playerId] || 'GENESIS';
    const existing = byPrevious.get(previous);
    if (existing && existing.certificateHash !== certificate.certificateHash) {
      forks.push({
        playerId,
        chainPrev: previous === 'GENESIS' ? null : previous,
        certificateHashes: [existing.certificateHash, certificate.certificateHash]
      });
    } else {
      byPrevious.set(previous, certificate);
    }
  }
  return forks;
}

function deepFreeze(value) {
  if (!value || typeof value !== 'object' || Object.isFrozen(value)) {
    return value;
  }
  Object.values(value).forEach(deepFreeze);
  return Object.freeze(value);
}
